import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast, Alert } from '@vnetwork/ui/feedback';
import {
  useAddAdmin,
  useContacts,
  orgErrorMessage,
  type AdminRole,
} from '@/api/organization';

interface AddAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ROLES: { value: AdminRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
];

/**
 * Add an organization administrator. Only contacts that already have a login
 * (and therefore a `user_uuid`) can become admins, so the picker is sourced
 * from the contacts query, filtered to those eligible contacts.
 */
export function AddAdminDialog({ open, onOpenChange }: AddAdminDialogProps) {
  const { show } = useToast();
  const { data: contacts } = useContacts({ page: 1, pageSize: 100 });
  const [userUuid, setUserUuid] = useState('');
  const [role, setRole] = useState<AdminRole>('admin');
  const [error, setError] = useState<string | undefined>();

  const eligible = useMemo(
    () => (contacts?.results ?? []).filter((c) => c.has_login && c.user_uuid),
    [contacts],
  );

  useEffect(() => {
    if (open) {
      setUserUuid('');
      setRole('admin');
      setError(undefined);
    }
  }, [open]);

  const add = useAddAdmin({
    onSuccess: () => {
      show({ tone: 'success', title: 'Administrator added' });
      onOpenChange(false);
    },
    onError: (err) =>
      show({ tone: 'danger', title: orgErrorMessage(err, 'Could not add administrator') }),
  });

  function handleSubmit() {
    if (!userUuid) {
      setError('Select a contact.');
      return;
    }
    setError(undefined);
    add.mutate({ user_uuid: userUuid, role });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Add administrator</DialogTitle>
          <DialogDescription>
            Grant a contact with a login administrative access to your organization.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="add-admin-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            {eligible.length === 0 ? (
              <Alert tone="warning" title="No eligible contacts">
                Only contacts with a login can become administrators. Provision a login for a
                contact first.
              </Alert>
            ) : (
              <Field label="Contact" required error={error}>
                {() => (
                  <Select value={userUuid} onValueChange={setUserUuid}>
                    <SelectTrigger invalid={!!error}>
                      <SelectValue placeholder="Select a contact" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligible.map((c) => (
                        <SelectItem key={c.user_uuid} value={c.user_uuid}>
                          {c.contact_name}
                          {c.contact_email ? ` (${c.contact_email})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}

            <Field label="Role" required>
              {() => (
                <Select value={role} onValueChange={(v) => setRole(v as AdminRole)}>
                  <SelectTrigger disabled={eligible.length === 0}>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-admin-form"
            disabled={add.isPending || eligible.length === 0}
          >
            {add.isPending ? 'Adding…' : 'Add administrator'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
